import React, {useEffect, useState} from 'react';
import './Loyalty.css';
import RedeemOutlinedIcon from '@mui/icons-material/RedeemOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import { DateTime } from "luxon";
import { inject, observer } from "mobx-react";

const LoyaltyBar = ({campaign, endsIn, days, feathersStore}) => {

  const [pointer, setPointer] = useState(0);
  const [wayPoints, setWayPoints] = useState([]);
  const [biggestWayPoint, setBiggestWayPoint] = useState(0)
  //Where the fill stops, kept so the progress badge can count up to the REAL turnover rather than
  //to a percentage. Without it the badge has no way to map its 0..pointer travel onto a figure.
  const [animationEnd, setAnimationEnd] = useState(0);


  useEffect(()=>{
    setWayPoints(campaign.wayPoints)
    const BIGGEST_WAYPOINT = findBiggestWayPoint(campaign);
    setBiggestWayPoint(BIGGEST_WAYPOINT);
    let ANIMATION_END = ((campaign?.turnover || campaign?.wayPoints[0]?.turnover) / BIGGEST_WAYPOINT) * 100;
    if(ANIMATION_END > 100)ANIMATION_END = 100;
    setAnimationEnd(ANIMATION_END);
    let _pointer = 0;
    //Stop when it arrives. Only the setPointer call was guarded before, not the interval, so this
    //kept firing every 100ms for the life of the page doing nothing. The final setPointer lands the
    //bar exactly on ANIMATION_END instead of up to 1% short of it.
    const pointerInterval = setInterval(() => {
      _pointer++;
      if(_pointer <= ANIMATION_END) setPointer(_pointer);
      else { setPointer(ANIMATION_END); clearInterval(pointerInterval); }
    }, 100)
  return(() => pointerInterval && clearInterval(pointerInterval))
  }, [campaign]);

  const findBiggestWayPoint = (campaign) => {
      return campaign?.wayPoints[campaign?.wayPoints?.length - 1].completion
  }

  //biggestWayPoint is 0 on the first render, before the effect above has run - an unguarded divide
  //returns Infinity and puts every marker at `left: Infinity%`.
  const findWayPointPercent = completion => {
    return biggestWayPoint ? (completion / biggestWayPoint) * 100 : 0;
  }

  const daysToCampaignEnd = (camp) => {
    const end = camp?.wayPoints[0]?.endDate ?  DateTime.fromISO(camp?.wayPoints[0]?.endDate)
      : DateTime.fromISO(camp?.wayPoints[0]?.startDate).plus({days : camp?.wayPoints[0].duration});
    const now = DateTime.now().setZone(feathersStore.timezone()).startOf('day');
    return Math.ceil(end.diff(now, 'days').toObject().days);
  }

  const parse_fix = (price) => {
    if(!price)price = 0;
    return price.toFixed(2);
  }

  //The customer's progress so far - euros, or PIECES on a Bonus campaign.
  const _turnover = campaign?.turnover ?? campaign?.wayPoints?.[0]?.turnover ?? 0;
  const _unit = campaign?.wayPoints?.[0]?.unit;
  //Counts up with the bar and lands on the real figure. It used to render turnover * pointer / 100,
  //but `pointer` is a PERCENTAGE, not a 0..1 ratio - so it settled on turnover squared over target:
  //a customer 30 euros into a 100 euro campaign was told they had 9.00, and a Bonus customer 1 piece
  //into a 2-piece campaign was shown "0.50 pieces".
  const _shown = animationEnd > 0 ? Math.min(_turnover, _turnover * pointer / animationEnd) : 0;

  const Pointer = ({style}) => (
    <div style={style} className='pointer-container'>
      {_unit === 'Piece' ? `${Math.round(_shown)}` : feathersStore.formatMoney(parse_fix(_shown))}
    </div>
  )

  const Line = ({style}) => (
    <div style={style} className='animated-line'>
    </div>
  )

  const WayPoint = ({style, percent, completion, reward, promotionText, type, unit}) => (
    <div style={style} className='loyalty-way-point-container'>
      <div  className='loyalty-way-icon-plus-caption'>
        <div style={ {backgroundColor: pointer >= percent ? "rgb(0, 145, 75)" : ""}}  className='loyalty-way-point-icon'>
          {pointer >= percent ?
            <CheckOutlinedIcon style={{fontSize: 20, color: "#fff"}}/>
            :
            <RedeemOutlinedIcon style={{fontSize: 20, color: "#fff"}}/>
          }
        </div>
        <div className='loyalty-way-point-caption'>
          {`${unit === 'Piece' ? completion : feathersStore.formatMoney(completion)}`}
        </div>
      </div>
      <div className='loyalty-way-point-bonus'>
        {`${type === 'Percent' ? `${reward}%` : (unit === 'Piece' ? `${reward}` : feathersStore.formatMoney(reward))}`}
      </div>
      {/* Rendered only when there is something to say. `display` defaults to FALSE on every new
          campaign, and an unticked box sends promotionText through as "" - which still emitted the
          dark pill and its arrow, so the default state of a campaign drew a small empty blob beside
          every marker. */}
      {promotionText ? (
        <div className='promotion-text-container'>
          <div className='promotion-text'>{promotionText}</div>
        </div>
      ) : null}
    </div>

  )

  return(
    <>
      {/* Out of the bar's overlay and into normal flow. It was absolutely positioned into the same
          band as the progress badge and the reward pill, and the two media queries then moved it
          DOWN (-110px to -55px / -50px) without moving anything else - so on a phone it covered most
          of the reward pill and took a bite out of the badge. It is opaque and z-index 15, so it won
          every one of those collisions. */}
      <div className="days-till-end">{`${endsIn} ${daysToCampaignEnd(campaign)} ${days}`}</div>
      <div className="loyalty-bar-container">
        <Pointer style={{ left: `${pointer}%`}}></Pointer>
        <Line style={{ width: `${pointer}%`}}></Line>
        {/* The box is centred on its point by transform: translate(-50%,-50%) in the CSS. It ALSO
            carried a `- 95px` fudge, so every marker landed 95px left of its target: a fully
            complete campaign drew its icon at 77% of a bar whose fill ran to 100%, and any waypoint
            below about a third of the biggest completion went NEGATIVE and hung off the left end.
            The fudge existed because the :first-of-type / :last-of-type edge rules meant to keep end
            markers on screen never matched - the first and last divs in this container are the
            pointer and the days label, not a waypoint. Those rules are gone; the bar's own side
            margins give an end marker the room it needs. */}
        { wayPoints?.map((wayPoint, index) => (
            <WayPoint
              key={index}
              style={{ left: `${findWayPointPercent(wayPoint.completion)}%` }}
              percent = {findWayPointPercent(wayPoint.completion)}
              completion = {wayPoint.completion}
              reward = {wayPoint.reward}
              promotionText ={wayPoint?.display ? (wayPoint?.promotionText || "") : ""}
              type = {wayPoint.type}
              unit = {wayPoint.unit}
            >
            </WayPoint>
          ))
        }
      </div>
    </>
  )

}

export default inject('feathersStore')(observer(LoyaltyBar));
